import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import type {
  DiagnosticComparison as DiagnosticComparisonType,
  DiagnosticFeedbackValue,
  DiagnosticFeedback,
} from "@shared/discovery-types";

interface Props {
  comparison: DiagnosticComparisonType;
  existingFeedback?: DiagnosticFeedback | null;
  onSubmitFeedback: (feedback: DiagnosticFeedback) => void;
  isSubmitting?: boolean;
}

const MATCH_COLORS: Record<string, string> = {
  identical: "text-[var(--success)]",
  partial: "text-[var(--warning)]",
  different: "text-destructive",
  one_missing: "text-muted-foreground",
};

const FEEDBACK_OPTIONS: { value: DiagnosticFeedbackValue; label: string }[] = [
  { value: "census_better", label: "Census better" },
  { value: "holistic_better", label: "Holistic better" },
  { value: "both_fine", label: "Both fine" },
  { value: "both_wrong", label: "Both wrong" },
];

function FeedbackButtons({
  section,
  selected,
  onChange,
}: {
  section: string;
  selected?: DiagnosticFeedbackValue;
  onChange: (section: string, value: DiagnosticFeedbackValue) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1 mt-2">
      {FEEDBACK_OPTIONS.map(opt => (
        <Button
          key={opt.value}
          variant={selected === opt.value ? "default" : "outline"}
          size="sm"
          className="text-xs h-7"
          aria-label={`Mark ${opt.label} for ${section}`}
          onClick={() => onChange(section, opt.value)}
        >
          {opt.label}
        </Button>
      ))}
    </div>
  );
}

function EntitySection({
  title,
  data,
  section,
  feedback,
  onFeedback,
}: {
  title: string;
  data: DiagnosticComparisonType["bankAccounts"];
  section: string;
  feedback?: DiagnosticFeedbackValue;
  onFeedback: (section: string, value: DiagnosticFeedbackValue) => void;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center justify-between">
          {title}
          <div className="flex gap-2 text-xs font-normal">
            <Badge variant="outline">Census: {data.totalCensus}</Badge>
            <Badge variant="outline">Holistic: {data.totalHolistic}</Badge>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="text-xs space-y-1">
        {data.both.length > 0 && (
          <div className="text-[var(--success)]">
            Both: {data.both.join(", ")}
          </div>
        )}
        {data.censusOnly.length > 0 && (
          <div className="text-[var(--warning)]">
            Census only: {data.censusOnly.join(", ")}
          </div>
        )}
        {data.holisticOnly.length > 0 && (
          <div className="text-[var(--info)]">
            Holistic only: {data.holisticOnly.join(", ")}
          </div>
        )}
        {data.both.length === 0 &&
          data.censusOnly.length === 0 &&
          data.holisticOnly.length === 0 && (
            <div className="text-muted-foreground">No entities found</div>
          )}
        <FeedbackButtons
          section={section}
          selected={feedback}
          onChange={onFeedback}
        />
      </CardContent>
    </Card>
  );
}

export function DiagnosticComparisonView({
  comparison,
  existingFeedback,
  onSubmitFeedback,
  isSubmitting,
}: Props) {
  const [feedback, setFeedback] = useState<DiagnosticFeedback>(
    existingFeedback || {}
  );
  const [notes, setNotes] = useState(existingFeedback?.notes || "");

  const handleFeedback = (section: string, value: DiagnosticFeedbackValue) => {
    setFeedback(prev => ({ ...prev, [section]: value }));
  };

  const handleSubmit = () => {
    onSubmitFeedback({ ...feedback, notes: notes || undefined });
  };

  const {
    companyIdentity: ci,
    transactions: tx,
    coverage,
    timing,
  } = comparison;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Pipeline Comparison</h3>
        <div className="flex gap-2 text-xs text-muted-foreground">
          <span>Census: {(timing.censusFirstMs / 1000).toFixed(1)}s</span>
          <span>Holistic: {(timing.holisticMs / 1000).toFixed(1)}s</span>
        </div>
      </div>

      {/* Company Identity */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center justify-between">
            Company Identity
            <Badge variant="outline" className={MATCH_COLORS[ci.match]}>
              {ci.match}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="text-xs">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="font-medium mb-1">Census-First</div>
              <div>Name: {ci.census.name || "\u2014"}</div>
              <div>UID: {ci.census.uid || "\u2014"}</div>
              <div>Legal: {ci.census.legalForm || "\u2014"}</div>
            </div>
            <div>
              <div className="font-medium mb-1">Holistic</div>
              <div>Name: {ci.holistic.name || "\u2014"}</div>
              <div>UID: {ci.holistic.uid || "\u2014"}</div>
              <div>Legal: {ci.holistic.legalForm || "\u2014"}</div>
            </div>
          </div>
          <FeedbackButtons
            section="companyIdentity"
            selected={feedback.companyIdentity}
            onChange={handleFeedback}
          />
        </CardContent>
      </Card>

      {/* Entity Sections */}
      <EntitySection
        title="Bank Accounts"
        data={comparison.bankAccounts}
        section="bankAccounts"
        feedback={feedback.bankAccounts}
        onFeedback={handleFeedback}
      />
      <EntitySection
        title="Vendors"
        data={comparison.vendors}
        section="vendors"
        feedback={feedback.vendors}
        onFeedback={handleFeedback}
      />
      <EntitySection
        title="Customers"
        data={comparison.customers}
        section="customers"
        feedback={feedback.customers}
        onFeedback={handleFeedback}
      />

      {/* Transactions */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center justify-between">
            Transactions
            <Badge variant={tx.amountsMatch ? "outline" : "destructive"}>
              {tx.amountsMatch ? "Amounts match" : "Amounts differ"}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="text-xs">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="font-medium mb-1">
                Census-First ({tx.censusCount})
              </div>
              <div>Debit: CHF {(tx.censusTotalDebit / 100).toFixed(2)}</div>
              <div>Credit: CHF {(tx.censusTotalCredit / 100).toFixed(2)}</div>
            </div>
            <div>
              <div className="font-medium mb-1">
                Holistic ({tx.holisticCount})
              </div>
              <div>Debit: CHF {(tx.holisticTotalDebit / 100).toFixed(2)}</div>
              <div>Credit: CHF {(tx.holisticTotalCredit / 100).toFixed(2)}</div>
            </div>
          </div>
          <FeedbackButtons
            section="transactions"
            selected={feedback.transactions}
            onChange={handleFeedback}
          />
        </CardContent>
      </Card>

      {/* Coverage (Census-First only) */}
      {(coverage.qualityGrade !== "N/A" || coverage.gaps.length > 0) && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">
              Census-First Coverage Analysis
            </CardTitle>
          </CardHeader>
          <CardContent className="text-xs space-y-1">
            <div>Quality Grade: {coverage.qualityGrade}</div>
            <div>Validation Issues: {coverage.validationIssues}</div>
            {coverage.gaps.length > 0 && (
              <div>Gaps: {coverage.gaps.join(", ")}</div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Notes + Submit */}
      <div className="space-y-2">
        <Textarea
          placeholder="Additional observations..."
          value={notes}
          onChange={e => setNotes(e.target.value)}
          className="text-sm"
          rows={3}
        />
        <Button onClick={handleSubmit} disabled={isSubmitting}>
          {isSubmitting ? "Saving..." : "Submit Feedback"}
        </Button>
      </div>
    </div>
  );
}
